import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Chip,
    CircularProgress,
    Checkbox,
    TableRow,
    TableCell,
    Button,
    FormControlLabel
} from "@mui/material";
import {
    Search as SearchIcon,
    Close as CloseIcon,
    Business as BusinessIcon
} from "@mui/icons-material";
import Dialog from "./Dialog";
import ModernDataTable from "../ModernDataTable";
import { getCompanies } from "../../services/system/CompanyService";
import { RTL } from "../../cache/CacheRtl";

/**
 * Reusable component for searching and selecting multiple companies
 *
 * Props:
 * - isOpen: boolean - Whether the dialog is open
 * - onClose: () => void - Callback when dialog is closed
 * - onConfirm: (selectedCompanies: Company[], isAllCompanies: boolean) => void - Callback when companies are confirmed
 * - title: string - Dialog title (default: "בחירת חברות")
 * - confirmButtonText: string - Text for confirm button (default: "אישור")
 * - isLoading: boolean - External loading state (optional)
 * - excludeCompanyIds: number[] - Company IDs to exclude from the list (optional)
 * - showAllCompaniesOption: boolean - Whether to show the "All Companies" checkbox (optional, default: true)
 * - initialSelectedCompanies: Company[] - Initial selected companies (optional)
 * - initialIsAllCompanies: boolean - Initial "all companies" state (optional)
 */
const CompanyMultiSelectDialog = ({
    isOpen,
    onClose,
    onConfirm,
    title = "בחירת חברות",
    confirmButtonText = "אישור",
    isLoading: externalLoading = false,
    excludeCompanyIds = [],
    showAllCompaniesOption = true,
    initialSelectedCompanies = [],
    initialIsAllCompanies = false
}) => {
    // State for companies list
    const [companiesState, setCompaniesState] = useState({
        companies: [],
        isLoading: false,
        totalNumberOfElements: 0
    });

    // State for selected companies
    const [selectedCompanies, setSelectedCompanies] = useState(initialSelectedCompanies);

    // State for "All Companies" option
    const [isAllCompanies, setIsAllCompanies] = useState(initialIsAllCompanies);

    // State for search
    const [keyword, setKeyword] = useState("");

    // Ref to track if initial load has happened
    const initialLoadDone = useRef(false);

    // Fetch companies function
    const fetchCompanies = useCallback(() => {
        setCompaniesState(prev => ({
            ...prev,
            isLoading: true
        }));

        getCompanies()
            .then(res => {
                let companies = res.data || [];

                // Filter by keyword if provided
                if (keyword) {
                    companies = companies.filter(company =>
                        company.name.toLowerCase().includes(keyword.toLowerCase())
                    );
                }

                // Filter out excluded companies
                companies = companies.filter(
                    company => !excludeCompanyIds.includes(company.id)
                );

                setCompaniesState({
                    companies,
                    isLoading: false,
                    totalNumberOfElements: companies.length
                });
            })
            .catch(err => {
                console.error("Error fetching companies:", err);
                setCompaniesState(prev => ({
                    ...prev,
                    isLoading: false
                }));
            });
    }, [keyword, excludeCompanyIds]);

    // Initial fetch when dialog opens
    useEffect(() => {
        if (isOpen && !initialLoadDone.current) {
            initialLoadDone.current = true;
            setSelectedCompanies(initialSelectedCompanies);
            setIsAllCompanies(initialIsAllCompanies);
            fetchCompanies();
        } else if (!isOpen) {
            // Reset state when dialog closes
            initialLoadDone.current = false;
            setCompaniesState({
                companies: [],
                isLoading: false,
                totalNumberOfElements: 0
            });
            setKeyword("");
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Refetch when keyword changes (with debounce)
    useEffect(() => {
        if (!isOpen || !initialLoadDone.current) {
            return;
        }

        const timer = setTimeout(() => {
            fetchCompanies();
        }, 300);

        return () => clearTimeout(timer);
    }, [keyword]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle company selection toggle
    const toggleCompanySelection = (company) => {
        if (isAllCompanies) return; // Don't allow individual selection when "all companies" is selected

        setSelectedCompanies(prev => {
            const isSelected = prev.some(c => c.id === company.id);
            if (isSelected) {
                return prev.filter(c => c.id !== company.id);
            } else {
                return [...prev, company];
            }
        });
    };

    // Remove company from selection
    const removeCompany = (companyId) => {
        setSelectedCompanies(prev => prev.filter(c => c.id !== companyId));
    };

    // Handle "All Companies" toggle
    const handleAllCompaniesToggle = (checked) => {
        setIsAllCompanies(checked);
        if (checked) {
            setSelectedCompanies([]);
        }
    };

    // Handle confirm
    const handleConfirm = () => {
        onConfirm(selectedCompanies, isAllCompanies);
    };

    // Check if confirm button should be enabled
    const isConfirmEnabled = isAllCompanies || selectedCompanies.length > 0;

    const columns = [
        { header: "", field: "checkbox", width: "10%", align: "center" },
        { header: "שם החברה", field: "name", width: "90%", align: "right" }
    ];

    const dialogActions = (
        <Box sx={{ display: "flex", gap: 2, width: "100%", justifyContent: "flex-start" }}>
            <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                    color: "#64748B",
                    borderColor: "#E2E8F0",
                    "&:hover": {
                        borderColor: "#CBD5E1",
                        backgroundColor: "#F8FAFC"
                    }
                }}
            >
                ביטול
            </Button>
            <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={!isConfirmEnabled || externalLoading}
                sx={{
                    backgroundColor: "#57837E",
                    "&:hover": {
                        backgroundColor: "#4a726d"
                    },
                    "&:disabled": {
                        backgroundColor: "#E2E8F0"
                    }
                }}
            >
                {externalLoading ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                ) : (
                    isAllCompanies
                        ? `${confirmButtonText} (כל החברות)`
                        : `${confirmButtonText} (${selectedCompanies.length})`
                )}
            </Button>
        </Box>
    );

    return (
        <Dialog
            title={title}
            isOpen={isOpen}
            close={onClose}
            actions={dialogActions}
            showCloseButton={true}
        >
            <Box sx={{ direction: "rtl", minHeight: "400px" }}>
                {/* All Companies Option */}
                {showAllCompaniesOption && (
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 2,
                        backgroundColor: isAllCompanies ? "#F0FDF4" : "#F8FAFC",
                        borderRadius: "8px",
                        mb: 2,
                        border: isAllCompanies ? "1px solid #57837E" : "1px solid #E2E8F0"
                    }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isAllCompanies}
                                    onChange={(e) => handleAllCompaniesToggle(e.target.checked)}
                                    sx={{
                                        color: "#64748B",
                                        "&.Mui-checked": {
                                            color: "#57837E"
                                        }
                                    }}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: "14px", color: "#1E293B", fontWeight: isAllCompanies ? 600 : 400 }}>
                                    כל החברות (כולל חברות חדשות שיצטרפו בעתיד)
                                </Typography>
                            }
                        />
                    </Box>
                )}

                {/* Selected companies chips */}
                {!isAllCompanies && selectedCompanies.length > 0 && (
                    <Box sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        p: 2,
                        backgroundColor: "#F0FDF4",
                        borderRadius: "8px",
                        mb: 2
                    }}>
                        <Typography sx={{
                            fontSize: "13px",
                            color: "#166534",
                            fontWeight: 600,
                            width: "100%",
                            mb: 1
                        }}>
                            נבחרו {selectedCompanies.length} חברות:
                        </Typography>
                        {selectedCompanies.map(company => (
                            <RTL key={company.id}>
                                <Chip
                                    label={company.name}
                                    onDelete={() => removeCompany(company.id)}
                                    deleteIcon={<CloseIcon sx={{ fontSize: "14px !important" }} />}
                                    icon={<BusinessIcon sx={{ fontSize: "14px !important" }} />}
                                    size="small"
                                    sx={{
                                        backgroundColor: "#DCFCE7",
                                        color: "#166534",
                                        fontSize: "12px",
                                        "& .MuiChip-deleteIcon": {
                                            color: "#166534",
                                            "&:hover": {
                                                color: "#14532D"
                                            }
                                        }
                                    }}
                                />
                            </RTL>
                        ))}
                    </Box>
                )}

                {/* Search */}
                {!isAllCompanies && (
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            placeholder="חיפוש לפי שם חברה..."
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            size="small"
                            fullWidth
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    backgroundColor: "#F8FAFC",
                                    borderRadius: "8px",
                                    "& fieldset": {
                                        borderColor: "#E2E8F0"
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#CBD5E1"
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#57837E"
                                    }
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "#64748B", fontSize: "20px" }} />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>
                )}

                {/* Companies table */}
                {!isAllCompanies && (
                    <Box sx={{ maxHeight: "300px", overflow: "auto" }}>
                        <ModernDataTable
                            data={companiesState.companies}
                            columns={columns}
                            totalCount={companiesState.totalNumberOfElements}
                            isLoading={companiesState.isLoading}
                            hasMore={false}
                            useInfiniteScroll={false}
                            emptyTitle="לא נמצאו חברות"
                            emptySubtitle="נסה לשנות את החיפוש"
                            containerSx={{ margin: 0 }}
                        >
                            {companiesState.companies.map((company) => (
                                <TableRow
                                    key={company.id}
                                    onClick={() => toggleCompanySelection(company)}
                                    sx={{
                                        cursor: "pointer",
                                        backgroundColor: selectedCompanies.some(c => c.id === company.id)
                                            ? "#F0FDF4"
                                            : "transparent",
                                        "&:hover": {
                                            backgroundColor: selectedCompanies.some(c => c.id === company.id)
                                                ? "#DCFCE7"
                                                : "#F8FAFC"
                                        },
                                        borderBottom: "1px solid #E2E8F0"
                                    }}
                                >
                                    <TableCell
                                        width="10%"
                                        align="center"
                                        sx={{ padding: "12px 8px", borderBottom: "none" }}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={selectedCompanies.some(c => c.id === company.id)}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleCompanySelection(company);
                                            }}
                                            sx={{
                                                color: "#64748B",
                                                "&.Mui-checked": {
                                                    color: "#57837E"
                                                }
                                            }}
                                        />
                                    </TableCell>

                                    <TableCell
                                        width="90%"
                                        align="right"
                                        sx={{ padding: "12px 16px", borderBottom: "none" }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <BusinessIcon sx={{ color: "#64748B", fontSize: "20px" }} />
                                            <Typography
                                                sx={{
                                                    fontSize: "14px",
                                                    fontWeight: 600,
                                                    color: "#1E293B"
                                                }}
                                            >
                                                {company.name}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </ModernDataTable>

                        {/* Loading indicator */}
                        {companiesState.isLoading && (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                <CircularProgress size={24} sx={{ color: "#57837E" }} />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};

export default CompanyMultiSelectDialog;
